import * as React from "react";
import type { SAAgent } from "@/data/sellerAnalysisTypes";
import { Reveal, Overline } from "@/components/presentation/primitives";

interface Props {
  agent: SAAgent;
  headline?: string;
  body?: string;
}

function displayPhone(raw?: string): string | null {
  if (!raw) return null;
  const digits = raw.replace(/[^\d]/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length === 10)
    return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  return raw;
}

export const SA_CTA: React.FC<Props> = ({ agent, headline, body }) => {
  const telHref = agent.phone ? `tel:${agent.phone.replace(/\s/g, "")}` : null;
  const emailHref = agent.email ? `mailto:${agent.email}` : null;
  const scheduleHref = agent.schedulingUrl || null;
  const phoneLabel = displayPhone(agent.phone);

  const primary =
    "inline-flex w-full items-center justify-center rounded-full bg-brass px-8 py-4 text-sm font-medium uppercase tracking-[0.16em] text-charcoal transition-colors duration-300 hover:bg-brass-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-ivory sm:w-auto";
  const secondary =
    "inline-flex w-full items-center justify-center rounded-full border border-charcoal/25 px-8 py-4 text-sm font-medium uppercase tracking-[0.16em] text-charcoal transition-colors duration-300 hover:border-charcoal hover:bg-charcoal hover:text-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-charcoal focus-visible:ring-offset-2 focus-visible:ring-offset-ivory sm:w-auto";

  return (
    <section
      className="w-full bg-ivory py-24 md:py-32"
      data-testid="sa-cta"
      aria-label="Get in touch"
    >
      <div className="mx-auto w-full max-w-3xl px-6 text-center md:px-10">
        <Reveal>
          <Overline className="mx-auto">Next step</Overline>
          <h2 className="mt-5 font-serif text-4xl leading-tight text-charcoal md:text-5xl lg:text-6xl">
            {headline || "Let's talk about what selling could look like."}
          </h2>
          {body && (
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-charcoal-soft md:text-lg">
              {body}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mx-auto mt-10 max-w-md border-y border-charcoal/10 py-7">
            <p className="font-serif text-xl text-charcoal">{agent.name}</p>
            {(agent.title || agent.brokerage) && (
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-charcoal-muted">
                {[agent.title, agent.brokerage].filter(Boolean).join(" · ")}
              </p>
            )}
            {agent.address && (
              <p className="mt-2 text-sm text-charcoal-muted">{agent.address}</p>
            )}
            <div className="mt-3 flex flex-col items-center gap-1 text-sm">
              {telHref && phoneLabel && (
                <a
                  href={telHref}
                  className="tracking-wide text-charcoal transition-colors hover:text-brass"
                  data-testid="sa-contact-phone"
                >
                  {phoneLabel}
                </a>
              )}
              {emailHref && (
                <a
                  href={emailHref}
                  className="tracking-wide text-charcoal transition-colors hover:text-brass"
                  data-testid="sa-contact-email"
                >
                  {agent.email}
                </a>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            {scheduleHref && (
              <a
                href={scheduleHref}
                target="_blank"
                rel="noopener noreferrer"
                className={primary}
                data-testid="sa-cta-schedule"
                aria-label={`Schedule a conversation with ${agent.name}`}
              >
                Schedule a Conversation
              </a>
            )}
            {telHref && (
              <a
                href={telHref}
                className={scheduleHref ? secondary : primary}
                data-testid="sa-cta-call"
                aria-label={`Call ${agent.name}`}
              >
                Call Alain
              </a>
            )}
            {emailHref && (
              <a
                href={emailHref}
                className={secondary}
                data-testid="sa-cta-email"
                aria-label={`Email ${agent.name}`}
              >
                Email Alain
              </a>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
